import type {
  ChatFileUploadRequest,
  ChatFileUploadResponse,
  ChatFileUrlsRequest,
  ChatFileUrlsResponse,
  AddChatDayGuestsRequest,
  AddChatDayGuestsResponse,
  ChatDayInvitationsResponse,
  ApplicationDto,
  ApplicationState,
  AssistantSelectRequest,
  AutoReplyDto,
  CalendarEventDto,
  ConversationDetailsDto,
  CreateChatDayRequest,
  ConversationDocsDto,
  CtaClickRequest,
  DraftChatRequest,
  DraftChatResponse,
  DrawnSummaryDto,
  DrawSummaryBody,
  EditMessageRequest,
  EndChatRequest,
  HiringStatusWrite,
  InboxFacetsBody,
  InboxFacetsResult,
  InboxQueryBody,
  InboxQueryResult,
  ActiveJobDto,
  JobDetailDto,
  ListDto,
  ListMembersBody,
  DispatchFormBody,
  FormDispatchDto,
  FormDto,
  FormResponsesDto,
  LinkDispatchBody,
  ListFormResponsesBody,
  MessageHitsResponse,
  QuickReplySelectRequest,
  ReportRequest,
  ReorderListsBody,
  BoundaryErrorReport,
  ResumeRenderErrorReport,
  SaveAutoReplyRequest,
  SaveFormBody,
  SaveListBody,
  SaveNoteBody,
  SaveSummaryBody,
  SaveTemplateRequest,
  SetApplicantStatusRequest,
  SetApplicationStateRequest,
  SendJobRequest,
  SendJobResponse,
  StartChatRequest,
  StartChatResponse,
  TemplateDto,
  TranslateRequest,
  TranslateResponse,
} from '@chat-emp/shared';
import { apiFetch } from './api.js';
import { uiOrigin } from './bridge.js';

/**
 * Typed client for the BFF façade. The chat context panels and application-action
 * UI call these; they never see an upstream URL or credential — everything goes
 * through the same-origin /api/*.
 */
export function setApplicationState(id: string, state: ApplicationState): Promise<ApplicationDto> {
  return apiFetch<ApplicationDto>(`/api/applications/${encodeURIComponent(id)}/state`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ state } satisfies SetApplicationStateRequest),
  });
}

/** The company's live postings for the job-details picker. */
export function getActiveJobs(): Promise<ActiveJobDto[]> {
  return apiFetch<ActiveJobDto[]>('/api/jobs');
}

export function getJobDetail(id: string): Promise<JobDetailDto> {
  return apiFetch<JobDetailDto>(`/api/jobs/${encodeURIComponent(id)}`);
}

/** Live candidate + application + job data for the details panel. */
export function getConversationDetails(cid: string): Promise<ConversationDetailsDto> {
  return apiFetch<ConversationDetailsDto>(`/api/conversations/${encodeURIComponent(cid)}/details`);
}

/** Move the conversation's applicant to a hiring stage (shortlist/interview/hire). */
export function setApplicantStatus(cid: string, status: HiringStatusWrite): Promise<void> {
  return apiFetch<void>(`/api/conversations/${encodeURIComponent(cid)}/applicant-status`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status, ui_origin: uiOrigin() } satisfies SetApplicantStatusRequest),
  });
}

/** Report which AI suggestion the employer picked, so the upstream audit row
 *  records it. Tracking only: failures are swallowed, never surfaced. */
export function selectQuickReply(body: QuickReplySelectRequest): Promise<void> {
  return apiFetch<void>('/api/chat/quick-replies/select', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

/** Diagnostics only: a failed report is swallowed, never surfaced. */
export function reportResumeRenderError(body: ResumeRenderErrorReport): Promise<void> {
  return apiFetch<void>('/api/chat/resume-render-error', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

export function reportBoundaryError(body: BoundaryErrorReport): Promise<void> {
  return apiFetch<void>('/api/chat/boundary-error', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

export function translateText(body: TranslateRequest): Promise<TranslateResponse> {
  return apiFetch<TranslateResponse>('/api/chat/translate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Report which row of an assistant reading the employer took. Tracking only:
 *  failures are swallowed, because an employer who has just moved somebody to
 *  shortlist must not be shown an error about a metrics write. */
export function selectAssistantRow(body: AssistantSelectRequest): Promise<void> {
  return apiFetch<void>('/api/chat/assistant/select', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

/** End a conversation: the backend sends the closing message and stamps the
 *  channel closing (which starts the deletion countdown). `alsoRejected` labels
 *  the analytics when the caller is rejecting rather than plainly closing. */
export function endChat(cid: string, text: string, alsoRejected = false): Promise<void> {
  return apiFetch<void>('/api/chat/end-chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ channelId: cid, text, alsoRejected } satisfies EndChatRequest),
  });
}

/** Report the jobseeker in a channel, or one of their messages when `messageId` is set. */
export function submitReport(body: ReportRequest): Promise<void> {
  return apiFetch<void>('/api/chat/report', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Rejects with a 409 whose message says why when the message can no longer be edited. */
export function editMessage(body: EditMessageRequest): Promise<void> {
  return apiFetch<void>('/api/chat/edit-message', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function startChatFileUpload(body: ChatFileUploadRequest): Promise<ChatFileUploadResponse> {
  return apiFetch<ChatFileUploadResponse>('/api/chat/files', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function resolveChatFiles(body: ChatFileUrlsRequest): Promise<ChatFileUrlsResponse> {
  return apiFetch<ChatFileUrlsResponse>('/api/chat/files/urls', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Report a CTA button click. The BFF relays it to the backend, which records the
 * campaign-funnel event AND seals the row, so the grey survives a reload without
 * any host wiring. Fire-and-forget: a click that reached the button has already
 * done its visible job, and failing the report must not fail the click.
 *
 * The acting user is taken from the verified session server-side, never sent
 * from here.
 */
export function reportCtaClick(body: CtaClickRequest): Promise<void> {
  return apiFetch<void>('/api/cta-click', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

/** Message templates for the composer. */
export function listTemplates(): Promise<TemplateDto[]> {
  return apiFetch<TemplateDto[]>('/api/templates');
}

export function createTemplate(body: SaveTemplateRequest): Promise<TemplateDto> {
  return apiFetch<TemplateDto>('/api/templates', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function updateTemplate(id: string, body: SaveTemplateRequest): Promise<TemplateDto> {
  return apiFetch<TemplateDto>(`/api/templates/${id}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function deleteTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/api/templates/${id}`, { method: 'DELETE' });
}

/**
 * The inbox read model, behind the BFF. `viewer_id` is stamped server-side from
 * the session — the client neither sends nor can influence it.
 */
export function queryInbox(body: InboxQueryBody): Promise<InboxQueryResult> {
  return apiFetch('/api/inbox/query', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function facetInbox(body: InboxFacetsBody): Promise<InboxFacetsResult> {
  return apiFetch('/api/inbox/facets', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** The messages behind a search, so a result can show the words it matched. */
export function fetchMessageHits(text: string): Promise<MessageHitsResponse> {
  return apiFetch(`/api/inbox/message-hits?q=${encodeURIComponent(text)}`);
}

/**
 * Create-or-resume the conversation and post the first message, as one call.
 *
 * One round trip rather than create-then-send on purpose: there is no state in
 * between worth exposing, and a create that succeeded followed by a send that
 * failed would leave exactly the empty channel this whole flow exists to avoid.
 */
export function startChatApi(body: StartChatRequest): Promise<StartChatResponse> {
  return apiFetch<StartChatResponse>('/api/chat/start', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** The pane before a chat exists: an existing chat to open, or the applicant's stage. */
export function draftChatApi(body: DraftChatRequest): Promise<DraftChatResponse> {
  return apiFetch<DraftChatResponse>('/api/chat/draft', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** A hiring status write for an applicant with no chat yet. */
export function setDraftStatusApi(
  target: { applicant: { id: string }; job: { id: string } },
  status: HiringStatusWrite,
): Promise<void> {
  return apiFetch<void>('/api/chat/draft/status', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...target, status, ui_origin: uiOrigin() }),
  });
}

/** One of the company's postings into an existing chat, as a card. */
export function sendJobApi(body: SendJobRequest): Promise<SendJobResponse> {
  return apiFetch<SendJobResponse>('/api/chat/send-job', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Hand-picked conversation lists, stored per user by the BFF. Mounted at
 *  /api/lists, action-style routes. */
export function listLists(): Promise<ListDto[]> {
  return apiFetch<ListDto[]>('/api/lists');
}

/** Create when `body.id` is absent, update the style (name/icon/color) when
 *  present. Returns just the id (the backend's ListSaveResponse) — the caller
 *  already holds the rest optimistically. */
export function saveList(body: SaveListBody): Promise<{ id: number }> {
  return apiFetch<{ id: number }>('/api/lists/save', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function deleteList(id: number): Promise<void> {
  return apiFetch<void>('/api/lists/delete', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ id }),
  });
}

/** Set the user's lists to this order of ids. */
export function reorderLists(body: ReorderListsBody): Promise<void> {
  return apiFetch<void>('/api/lists/reorder', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function addToList(body: ListMembersBody): Promise<void> {
  return apiFetch<void>('/api/lists/members/add', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function removeFromList(body: ListMembersBody): Promise<void> {
  return apiFetch<void>('/api/lists/members/remove', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * What is written about one conversation: the employer's note and the drawn
 * summary, fetched together because the panel shows them together.
 *
 * The channel id travels in the body rather than the path — it is a Stream cid
 * and carries a `:` that would need escaping at every call site.
 */
export function getDocs(channelId: string): Promise<ConversationDocsDto> {
  return apiFetch<ConversationDocsDto>('/api/docs', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ channelId }),
  });
}

/** An empty `text` clears the note. The summary beside it is untouched. */
export function saveNote(body: SaveNoteBody): Promise<void> {
  return apiFetch<void>('/api/docs/note', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function saveSummary(body: SaveSummaryBody): Promise<void> {
  return apiFetch<void>('/api/docs/summary', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Draw a fresh summary without storing it. Keeping it is a separate saveSummary.
 * `signal` releases the request on cancel. Note it aborts only the call to the
 * BFF; the upstream draw it triggered still runs, and is still billed.
 */
export function drawSummary(body: DrawSummaryBody, signal?: AbortSignal): Promise<DrawnSummaryDto> {
  return apiFetch<DrawnSummaryDto>('/api/docs/summary/draw', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
}

/** What the employer has scheduled. See server/src/routes/events.ts. */
export function listEvents(): Promise<CalendarEventDto[]> {
  return apiFetch<CalendarEventDto[]>('/api/events');
}

/** File joined jobseekers into their Chat Day's list. See server/src/routes/chat-day.ts. */
export function syncChatDayLists(): Promise<{ changed: boolean }> {
  return apiFetch<{ changed: boolean }>('/api/chat-day/sync', { method: 'POST' });
}

/** Where Jobie's Chat Day cards stand for this company. See server/src/routes/chat-day.ts. */
export function fetchChatDayInvitations(
  inviteCodes: string[],
): Promise<ChatDayInvitationsResponse> {
  return apiFetch<ChatDayInvitationsResponse>('/api/chat-day/invitations', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ inviteCodes }),
  });
}

/** A CTA button whose url is a BFF route: sent with the session, so the BFF knows who tapped. */
export function postCtaAction(path: string, body: unknown): Promise<unknown> {
  return apiFetch<unknown>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * The one auto reply this account sends to a first message.
 *
 * No id in either direction: there is one reply per account, so the read takes
 * nothing and the write replaces the whole of it. An employer who has never
 * saved one reads back as *off* rather than as a 404 — the store answers its
 * zero value. See server/src/routes/auto-reply.ts.
 */
export function getAutoReply(): Promise<AutoReplyDto> {
  return apiFetch<AutoReplyDto>('/api/auto-reply');
}

/**
 * Save the reply, whole.
 *
 * `formId: null` is sent explicitly for "No form, message only": the write is a
 * whole-row upsert, so leaving the field out would clear the form by accident
 * rather than on purpose. Resolves with the reply as it was actually stored,
 * including any trim the store applied.
 */
export function saveAutoReply(reply: SaveAutoReplyRequest): Promise<AutoReplyDto> {
  return apiFetch<AutoReplyDto>('/api/auto-reply', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(reply),
  });
}

/**
 * Book a Chat Day: one appointment, every guest an invitee.
 *
 * The guests go up as the *channels* they were picked from, not as user ids —
 * the BFF derives who each one is from a channel it has proved this employer is
 * in, so this call cannot name somebody the employer has no chat with. The
 * employer is not sent at all; it comes from the verified session.
 *
 * Resolves with the appointment as it was actually stored.
 */
export function createChatDay(body: CreateChatDayRequest): Promise<CalendarEventDto> {
  return apiFetch<CalendarEventDto>('/api/events', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function addChatDayGuests(
  eventId: string,
  body: AddChatDayGuestsRequest,
): Promise<AddChatDayGuestsResponse> {
  return apiFetch<AddChatDayGuestsResponse>(`/api/events/${encodeURIComponent(eventId)}/guests`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ── forms (chat-kit lane) ────────────────────────────────────────────────────

const formsPost = { method: 'POST', headers: { 'content-type': 'application/json' } };

export function listForms(): Promise<FormDto[]> {
  return apiFetch<FormDto[]>('/api/forms/list', { ...formsPost, body: '{}' });
}

export function listFormTemplates(): Promise<FormDto[]> {
  return apiFetch<FormDto[]>('/api/forms/templates', { ...formsPost, body: '{}' });
}

export function saveForm(body: SaveFormBody): Promise<FormDto> {
  return apiFetch<FormDto>('/api/forms/save', { ...formsPost, body: JSON.stringify(body) });
}

export function deleteForm(formId: string): Promise<void> {
  return apiFetch<void>('/api/forms/delete', { ...formsPost, body: JSON.stringify({ formId }) });
}

/** Every answer to one form across chats: the Responses table's one read. */
export function listFormResponses(body: ListFormResponsesBody): Promise<FormResponsesDto> {
  return apiFetch<FormResponsesDto>('/api/forms/responses', {
    ...formsPost,
    body: JSON.stringify(body),
  });
}

/** Record the send with chat-kit and get the `form` payload to post on the
 *  Stream message — the client copies it verbatim, never assembles it. */
export function dispatchForm(body: DispatchFormBody): Promise<FormDispatchDto> {
  return apiFetch<FormDispatchDto>('/api/forms/dispatch', {
    ...formsPost,
    body: JSON.stringify(body),
  });
}

export function linkFormDispatch(body: LinkDispatchBody): Promise<void> {
  return apiFetch<void>('/api/forms/dispatch-link', {
    ...formsPost,
    body: JSON.stringify(body),
  });
}
